// Task 13 (WU-2.3) — supervisor compose dialog for directed notes.
// TeamDialog idiom (admin/teams/components/TeamDialog.tsx): window-level
// Escape listener + a char counter (NOT the ComposeAnnouncementDialog shape,
// which lacks both). `presetRecipient` skips the picker when the caller
// already knows who the note is for (e.g. a doc's assignedTo); otherwise the
// picker is fed by directedNotesApi.recipients() (scoped to the caller's team
// per the server route).
import { useEffect, useRef, useState } from "react";
import { DIRECTED_NOTE_BODY_MAX } from "@capture/shared";
import { toast } from "@/components/Toaster";
import { directedNotesApi } from "../api/client";
import styles from "../directed-notes.module.css";

export interface ComposeNoteDialogProps {
  /** Skip the recipient picker when set — used when a doc/row already has an assignee. */
  presetRecipient?: { id: string; name: string | null } | null;
  /** Anchors the note to a document (shown in the API payload + dialog header). */
  documentId?: string;
  /** e.g. the filename — shown in the dialog header when documentId is set. */
  documentLabel?: string;
  onClose: () => void;
  onSent: () => void;
}

export function ComposeNoteDialog({
  presetRecipient,
  documentId,
  documentLabel,
  onClose,
  onSent,
}: ComposeNoteDialogProps) {
  const [recipients, setRecipients] = useState<{ id: string; name: string | null }[]>([]);
  const [toUserId, setToUserId] = useState(presetRecipient?.id ?? "");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Alive-guard for submit()'s failure path — mirrors the `cancelled` flag
  // the recipients-fetch effect below uses, but as a ref since submit() is
  // invoked from a click handler (not an effect) and needs to persist across
  // the component's whole lifetime, not just one effect run.
  const mountedRef = useRef(true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (presetRecipient) return;
    let cancelled = false;
    directedNotesApi
      .recipients()
      .then((r) => {
        if (!cancelled) setRecipients(r.users);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load recipients");
      });
    return () => {
      cancelled = true;
    };
  }, [presetRecipient]);

  const canSubmit = !busy && toUserId !== "" && body.trim().length > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await directedNotesApi.create({
        toUserId,
        body: body.trim(),
        ...(documentId ? { documentId } : {}),
      });
      toast.success("Note sent");
      onSent();
    } catch {
      if (!mountedRef.current) return;
      setError("Could not send the note");
      setBusy(false);
    }
  }

  return (
    <div className={styles.modalBackdrop} role="dialog" aria-modal="true" aria-label="Send note">
      <div className={styles.modal}>
        <h2>Send note</h2>
        {documentLabel && <p className={styles.noteMeta}>About document: {documentLabel}</p>}
        {presetRecipient ? (
          <p className={styles.noteMeta}>To: {presetRecipient.name ?? presetRecipient.id}</p>
        ) : (
          <label className={styles.field}>
            To
            <select value={toUserId} onChange={(e) => setToUserId(e.target.value)}>
              <option value="">Select an operator…</option>
              {recipients.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ?? u.id}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className={styles.field}>
          Note
          <textarea
            value={body}
            maxLength={DIRECTED_NOTE_BODY_MAX}
            autoFocus
            onChange={(e) => setBody(e.target.value)}
          />
          <small className={styles.counter}>
            {body.length} / {DIRECTED_NOTE_BODY_MAX}
          </small>
        </label>
        {error && <p className={styles.error}>{error}</p>}
        <div className={styles.modalActions}>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" disabled={!canSubmit} onClick={() => void submit()}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
